import { motion } from "framer-motion";
import { Reveal, Chapter, SectionHead } from "@/components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

const RoadGlyph = () => (
  <svg viewBox="0 0 96 96" className="h-20 w-20" fill="none" aria-hidden="true">
    <path d="M38 88 L46 34 L50 34 L58 88" stroke="rgba(154,164,178,0.5)" strokeWidth="1.5" />
    <path d="M48 84 L48 74 M48 66 L48 56 M48 48 L48 40" stroke="#22D3EE" strokeWidth="1.5" strokeLinecap="round" className="route-flow" />
    <circle cx="48" cy="26" r="3" fill="#22D3EE" />
    <circle cx="48" cy="26" r="7" stroke="rgba(34,211,238,0.35)" strokeWidth="1" />
    <path d="M20 88 L40 30 M76 88 L56 30" stroke="rgba(154,164,178,0.25)" strokeWidth="1" />
  </svg>
);

const AiGlyph = () => (
  <svg viewBox="0 0 96 96" className="h-20 w-20" fill="none" aria-hidden="true">
    <rect x="28" y="28" width="40" height="40" rx="6" stroke="#7C5CFF" strokeWidth="1.5" />
    <rect x="40" y="40" width="16" height="16" rx="3" fill="rgba(124,92,255,0.2)" stroke="#7C5CFF" strokeWidth="1.5" />
    <path d="M28 40 H16 M28 56 H16 M68 40 H80 M68 56 H80 M40 28 V16 M56 28 V16 M40 68 V80 M56 68 V80" stroke="rgba(124,92,255,0.4)" strokeWidth="1.5" />
    <line x1="24" y1="34" x2="72" y2="34" stroke="rgba(34,211,238,0.7)" strokeWidth="1" className="scan-line" style={{ position: "relative" }} />
  </svg>
);

const VerifyGlyph = () => (
  <svg viewBox="0 0 96 96" className="h-20 w-20" fill="none" aria-hidden="true">
    <circle cx="48" cy="48" r="18" stroke="#34D399" strokeWidth="1.5" />
    <path d="M40 48 L46 54 L58 42" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="48" cy="48" r="27" stroke="rgba(52,211,153,0.25)" strokeWidth="1" strokeDasharray="3 6" />
    <circle cx="22" cy="30" r="2" fill="rgba(52,211,153,0.6)" />
    <circle cx="74" cy="66" r="2" fill="rgba(52,211,153,0.6)" />
  </svg>
);

const BiGlyph = () => (
  <svg viewBox="0 0 96 96" className="h-20 w-96" fill="none" aria-hidden="true" style={{ width: "5rem" }}>
    <path d="M24 76 V52 M40 76 V38 M56 76 V58 M72 76 V28" stroke="url(#bi-g)" strokeWidth="4" strokeLinecap="round" />
    <defs>
      <linearGradient id="bi-g" x1="0" y1="0" x2="96" y2="0" gradientUnits="userSpaceOnUse">
        <stop stopColor="#22D3EE" />
        <stop offset="1" stopColor="#7C5CFF" />
      </linearGradient>
    </defs>
    <path d="M20 24 H76" stroke="rgba(154,164,178,0.2)" strokeWidth="1" strokeDasharray="2 5" />
  </svg>
);

const STAGES = [
  {
    Glyph: RoadGlyph,
    title: "Physical world",
    text: "Contributors capture roads, vehicles and infrastructure with everyday devices.",
    accent: "text-signal-cyan",
  },
  {
    Glyph: AiGlyph,
    title: "AI understanding",
    text: "AI detects and classifies what changed - potholes, signs, lane markings, damage.",
    accent: "text-signal-violet",
  },
  {
    Glyph: VerifyGlyph,
    title: "Verified data",
    text: "Independent verifier nodes attest to quality, freshness and authenticity.",
    accent: "text-signal-green",
  },
  {
    Glyph: BiGlyph,
    title: "Business intelligence",
    text: "Structured, machine-readable data streams to applications and enterprises.",
    accent: "text-mist",
  },
];

export const Concept = () => {
  return (
    <section id="how-it-works" className="relative py-28 sm:py-40 bg-ink-950" data-testid="section-how-it-works">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter label="How it works" />
        <SectionHead
          title={"From the physical world\nto business intelligence."}
          sub="Synaphy turns real-world observations into structured, verified intelligence. Four stages, one continuous pipeline."
          className="max-w-3xl"
        />

        <div className="relative mt-20 sm:mt-24">
          <div className="absolute left-0 right-0 top-10 hidden lg:block h-px bg-white/[0.07]" aria-hidden="true" />
          <svg className="absolute left-0 right-0 top-10 hidden lg:block h-px w-full" aria-hidden="true">
            <line x1="0" y1="0" x2="100%" y2="0" stroke="rgba(34,211,238,0.35)" strokeWidth="1" className="flow-line" />
          </svg>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-14 lg:gap-8">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.14, ease: EASE }}
                className="relative"
                data-testid={`how-step-${i}`}
              >
                {i < STAGES.length - 1 && (
                  <span
                    className="lg:hidden absolute left-[39px] top-[88px] w-px bg-gradient-to-b from-signal-cyan/40 via-white/10 to-transparent"
                    style={{ height: "calc(100% - 88px + 56px)" }}
                    aria-hidden="true"
                  />
                )}
                <div className="relative">
                  <div className="absolute left-1/2 top-10 hidden lg:block h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink-950 border border-signal-cyan/50" aria-hidden="true" />
                  <s.Glyph />
                </div>
                <div className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-zinc-600">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className={`mt-2 font-display text-xl font-semibold tracking-tight ${s.accent}`}>
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-steel max-w-xs">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
